<script setup lang="ts" generic="T extends Record<string, string>">
import { ref } from 'vue';
import { default as NxTableColumn, type NxTableColumnProps } from './NxTableColumn.vue';
import { NxTextInput } from './../Inputs';
import type { InlineIconProps } from './../Inputs/internal';
import { defaultEditCellFocusRef, onEditCellKeydown, PartialOrNull } from './internal';

const props = withDefaults(
  defineProps<
    Omit<NxTableColumnProps<T, keyof T, string>, 'toCsvString'> & {
      /** icons to show within the input cells */
      icons?: InlineIconProps[];
    }
  >(),
  {
    filterable: true,
    resizeable: true,
    reorderable: true,
    sortable: true,
    editable: true,
    defaultVisible: true,
    layout: 'row',
    icons: undefined,
  }
);

const filterCellModel = ref<string>('');
const createCellModel = ref<string>('');

function onQueryLoaded(value: string[]) {
  filterCellModel.value = value.length > 0 ? value[0] : '';
}
</script>

<template>
  <!-- @vue-generic {T, keyof T, string} -->
  <NxTableColumn
    v-bind="{
      ...props,
      filterKind: props.filterKind ?? 'string',
      onQueryLoaded: props.onQueryLoaded ? props.onQueryLoaded : onQueryLoaded,
      toCsvString: (rowData: T) => rowData[props.columnId],
    }"
  >
    <template v-if="$slots.summaryCell" #summaryCell="summaryProps">
      <slot name="summaryCell" v-bind="summaryProps"></slot>
    </template>
    <template v-if="$slots.headerCell" #headerCell>
      <slot name="headerCell"></slot>
    </template>
    <template
      #filterCell="{
        setFilterValue,
        disabled,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="filterCell"
        v-bind="{
          setFilterValue,
          disabled,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <NxTextInput
          :ref="defaultEditCellFocusRef(registerFocus)"
          v-model:model-value="filterCellModel"
          height="xs"
          placeholder=""
          :disabled="disabled"
          :icons="props.icons"
          @update:model-value="
            (value: string) => {
              setFilterValue(value ? [value] : []);
            }
          "
          @keydown="
            (e: KeyboardEvent) =>
              onEditCellKeydown(e, {
                focusAbove,
                focusBelow,
                focusLeft,
                focusRight,
                inputType: 'text',
              })
          "
        ></NxTextInput>
      </slot>
    </template>
    <template #cell="{ rowData, rowIndex }">
      <slot name="cell" v-bind="{ rowData, rowIndex }">
        <span class="one-line">{{ rowData[props.columnId] }}</span>
      </slot>
    </template>
    <template #createCell="{ setPartialRowData }">
      <slot name="createCell" v-bind="{ setPartialRowData }">
        <NxTextInput
          :model-value="createCellModel"
          :placeholder="`${props.displayName ?? (props.columnId as string)}`"
          :icons="props.icons"
          @update:model-value="
            (value: string) => {
              setPartialRowData({
                [props.columnId]: value,
              } as PartialOrNull<T>);
            }
          "
        ></NxTextInput>
      </slot>
    </template>
    <template
      #editCell="{
        rowData,
        rowIndex,
        onEdit,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="editCell"
        v-bind="{
          rowData,
          rowIndex,
          onEdit,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <div class="nx-ghost-cell">
          <NxTextInput
            :ref="defaultEditCellFocusRef(registerFocus)"
            :model-value="rowData[props.columnId] || ''"
            :model-modifiers="{ lazy: true }"
            :icons="props.icons"
            @update:model-value="
              (value) => {
                onEdit({
                  [props.columnId]: value,
                } as Partial<T>);
              }
            "
            @keydown="
              (e: KeyboardEvent) =>
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  inputType: 'text',
                })
            "
          ></NxTextInput>
        </div>
      </slot>
    </template>
  </NxTableColumn>
</template>

<style scoped>
.one-line {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  /* Cut long values with an ellipsis, never mid-letter. */
  text-overflow: ellipsis;
}
</style>
